import { useCallback, useEffect, useState } from 'react';
import { Alert, Tag } from 'antd';
import toast from 'react-hot-toast';
import {
  listActive,
  dismissAnnouncement,
  type Announcement,
} from '@/services/api/announcement.service';
import './AnnouncementBanner.css';

function alertTypeForPriority(priority: string): 'error' | 'warning' | 'info' | 'success' {
  const p = priority?.toLowerCase() ?? '';
  if (p === 'critical') return 'error';
  if (p === 'high') return 'warning';
  if (p === 'low') return 'success';
  return 'info';
}

const AnnouncementBanner = () => {
  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listActive();
      setItems(rows);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleClose = useCallback(
    async (id: string) => {
      try {
        await dismissAnnouncement(id);
        setItems((prev) => prev.filter((a) => a.id !== id));
      } catch {
        toast.error('Could not dismiss announcement');
      }
    },
    [],
  );

  if (loading || items.length === 0) {
    return null;
  }

  return (
    <div className="announcement-banner" role="region" aria-label="Announcements">
      {items.map((a) => (
        <Alert
          key={a.id}
          className="announcement-banner__alert"
          type={alertTypeForPriority(String(a.priority))}
          showIcon
          closable
          onClose={() => void handleClose(a.id)}
          message={
            <span className="announcement-banner__title-row">
              <span>{a.title}</span>
              <Tag className="announcement-banner__type-tag" color="default">
                {String(a.announcement_type).replace(/_/g, ' ')}
              </Tag>
            </span>
          }
          description={a.message}
        />
      ))}
    </div>
  );
};

export default AnnouncementBanner;
